import { Capacitor } from '@capacitor/core';

/** Same ceiling as illustration OCR (`MAX_ILLUSTRATION_BYTES`). */
const MAX_OCR_BYTES = 9 * 1024 * 1024;
const MAX_EDGE = 1568;
const MAX_DECISION_IMAGES = 4;
const MAX_PDF_DECISION_PAGES = 3;
const MAX_OCR_IMAGE_PAGES = 10;
const MAX_TEXT_CHARS = 4000;
const MAX_DECISION_IMAGE_BYTES = Math.floor(1.4 * 1024 * 1024);
const JPEG_QUALITIES = [0.8, 0.6, 0.45, 0.3];
const REMOTE_SRC = /^(https?:|data:|blob:)/i;

export const SHARE_FILE_PASSWORD_NEEDED = 'PDF_PASSWORD_NEEDED';
export const SHARE_FILE_PASSWORD_INCORRECT = 'PDF_PASSWORD_INCORRECT';
export const SHARE_FILE_TOO_LARGE = 'FILE_TOO_LARGE';

export type JpegPage = {
  blob: Blob;
  dataUrl: string;
};

export type PreparedIncomingShare = {
  decisionImages: string[];
  text: string;
  ocrFile: File;
  pdfPassword: string | null;
};

type ShareItem = {
  kind?: string;
  uri?: string;
  name?: string;
  mimeType?: string;
  text?: string;
};

export type SharedDocument = {
  name: string;
  mimeType: string;
  file: File;
};

export type PrepareIncomingShareOptions = {
  password?: string;
  readFile?: (uri: string) => Promise<Blob>;
  toJpeg?: (blob: Blob) => Promise<JpegPage>;
  renderPdf?: (buffer: ArrayBuffer, password?: string) => Promise<JpegPage[]>;
  mergeToPdf?: (pages: Blob[]) => Promise<Blob>;
};

export const shareFileError = (code: string) => {
  const error = new Error(code) as Error & { code: string };
  error.code = code;
  return error;
};

const bytesOf = (blob: Blob) => {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || shareFileError('UNREADABLE'));
    reader.readAsArrayBuffer(blob);
  });
};

const dataUrlBytes = (dataUrl: string) => {
  const comma = dataUrl.indexOf(',');
  const payload = comma >= 0 ? dataUrl.slice(comma + 1) : '';
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.floor((payload.length * 3) / 4) - padding;
};

const canvasToJpeg = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<JpegPage>((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(shareFileError('IMAGE_UNREADABLE'));
          return;
        }
        resolve({ blob, dataUrl: canvas.toDataURL('image/jpeg', quality) });
      },
      'image/jpeg',
      quality
    );
  });

export const downscaleToJpeg = (blob: Blob) =>
  new Promise<JpegPage>((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      const longest = Math.max(image.width || 1, image.height || 1);
      const scale = Math.min(1, MAX_EDGE / longest);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round((image.width || 1) * scale));
      canvas.height = Math.max(1, Math.round((image.height || 1) * scale));
      const context = canvas.getContext('2d');
      if (!context) {
        URL.revokeObjectURL(url);
        reject(shareFileError('IMAGE_UNREADABLE'));
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const encode = async () => {
        try {
          let encoded = await canvasToJpeg(canvas, JPEG_QUALITIES[0]);
          for (let index = 1; index < JPEG_QUALITIES.length; index += 1) {
            if (dataUrlBytes(encoded.dataUrl) <= MAX_DECISION_IMAGE_BYTES) break;
            encoded = await canvasToJpeg(canvas, JPEG_QUALITIES[index]);
          }
          if (dataUrlBytes(encoded.dataUrl) > MAX_DECISION_IMAGE_BYTES) {
            throw shareFileError(SHARE_FILE_TOO_LARGE);
          }
          resolve(encoded);
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      encode();
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(shareFileError('IMAGE_UNREADABLE'));
    };
    image.src = url;
  });

/** Absolute path inside a `file:` URI. Native cache files use this form. */
export const nativeFilePath = (uri: string) => {
  if (typeof uri !== 'string' || !uri.startsWith('file:')) return null;
  try {
    const pathname = decodeURIComponent(new URL(uri).pathname);
    return pathname || null;
  } catch (error) {
    return null;
  }
};

/**
 * Path passed to Capacitor Filesystem.readFile.
 * Android crashes the process when this has no URI scheme: Uri.getScheme() is
 * null and `.equals("content")` throws on the plugin thread. Keep `file:`.
 */
export const nativeReadPath = (uri: string, path: string, platform?: string) => {
  if (platform === 'android' && uri.startsWith('file:')) return uri;
  return path;
};

export const blobFromBase64 = (base64: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes]);
};

type ReadShareDeps = {
  isNative: boolean;
  platform?: string;
  readNative: (path: string) => Promise<string | Blob>;
  fetchFile: (url: string) => Promise<Blob>;
  convertFileSrc: (uri: string) => string;
};

/**
 * Read the document the user shared. On device, cache files are read through
 * the native filesystem. Browser previews and blob URLs stay on fetch.
 */
export const readShareFile = async (uri: string, deps: ReadShareDeps) => {
  const path = nativeFilePath(uri);
  if (deps.isNative && path) {
    try {
      const data = await deps.readNative(nativeReadPath(uri, path, deps.platform));
      if (data instanceof Blob) return data;
      if (typeof data === 'string' && data) return blobFromBase64(data);
    } catch (error) {
      // Some cache files are still readable through the web view file server.
    }
  }

  const url = REMOTE_SRC.test(uri) ? uri : deps.convertFileSrc(uri);
  return deps.fetchFile(url);
};

const defaultReadFile = (uri: string) =>
  readShareFile(uri, {
    isNative: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    readNative: async path => {
      const { Filesystem } = await import('@capacitor/filesystem');
      const result = await Filesystem.readFile({ path });
      return result.data;
    },
    convertFileSrc: fileUri => Capacitor.convertFileSrc(fileUri),
    fetchFile: async url => {
      const response = await fetch(url);
      if (!response.ok) throw shareFileError('UNREADABLE');
      return response.blob();
    }
  });

const renderPdfPage = async (page: {
  getViewport: (options: { scale: number }) => { width: number; height: number };
  render: (options: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void> };
}) => {
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(1.5, MAX_EDGE / Math.max(base.width || 1, base.height || 1));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const context = canvas.getContext('2d');
  if (!context) throw shareFileError('IMAGE_UNREADABLE');
  await page.render({ canvasContext: context, viewport }).promise;
  let encoded = await canvasToJpeg(canvas, JPEG_QUALITIES[0]);
  for (let index = 1; index < JPEG_QUALITIES.length; index += 1) {
    if (dataUrlBytes(encoded.dataUrl) <= MAX_DECISION_IMAGE_BYTES) break;
    encoded = await canvasToJpeg(canvas, JPEG_QUALITIES[index]);
  }
  if (dataUrlBytes(encoded.dataUrl) > MAX_DECISION_IMAGE_BYTES) throw shareFileError(SHARE_FILE_TOO_LARGE);
  return encoded;
};

const defaultRenderPdf = async (buffer: ArrayBuffer, password?: string) => {
  const pdfModule = await import('../../views/Advisor/IllustrationOcr/PdfPagePreviewSelector');
  let pdf: { numPages?: number; getPage: (pageNumber: number) => Promise<Parameters<typeof renderPdfPage>[0]> };
  try {
    pdf = await pdfModule.openPdfDocument(buffer, password);
  } catch (error) {
    const code = (error as { code?: string } | null)?.code;
    if (code === pdfModule.PDF_PASSWORD_NEEDED || code === pdfModule.PDF_PASSWORD_INCORRECT) {
      throw shareFileError(code);
    }
    throw error;
  }

  const count = Math.min(pdf.numPages || 0, MAX_PDF_DECISION_PAGES);
  if (!count) throw shareFileError('PDF_EMPTY');
  const pages: JpegPage[] = [];
  for (let pageNumber = 1; pageNumber <= count; pageNumber += 1) {
    pages.push(await renderPdfPage(await pdf.getPage(pageNumber)));
  }
  return pages;
};

const defaultMergeToPdf = async (pages: Blob[]) => {
  const { PDFDocument } = await import('@cantoo/pdf-lib');
  const document = await PDFDocument.create();
  for (const page of pages.slice(0, MAX_OCR_IMAGE_PAGES)) {
    const bytes = new Uint8Array(await bytesOf(page));
    const image = await document.embedJpg(bytes);
    const pdfPage = document.addPage([image.width, image.height]);
    pdfPage.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
  }
  const saved = await document.save();
  return new Blob([saved], { type: 'application/pdf' });
};

const jpegName = (name?: string) => {
  const base = (name || 'shared').replace(/\.[^.]+$/, '').trim();
  return `${base || 'shared'}.jpg`;
};

const assertOcrSize = (blob: Blob) => {
  if (incomingShareTooLarge(blob.size)) throw shareFileError(SHARE_FILE_TOO_LARGE);
};

/** Illustration OCR refuses anything over 9 MB. Shortcut input uses the same ceiling. */
export const incomingShareTooLarge = (size: number) => size > MAX_OCR_BYTES;

/** The original shared documents, without the OCR resize used by premium financing. */
export const readIncomingShareFiles = async (
  share: { items?: ShareItem[] } | null | undefined,
  options: { readFile?: (uri: string) => Promise<Blob> } = {}
): Promise<SharedDocument[]> => {
  const readFile = options.readFile || defaultReadFile;
  const items = Array.isArray(share?.items) ? share.items : [];
  const documents: SharedDocument[] = [];

  for (const item of items) {
    if ((item.kind !== 'image' && item.kind !== 'pdf') || typeof item.uri !== 'string' || !item.uri) continue;
    const blob = await readFile(item.uri);
    const mimeType =
      (typeof item.mimeType === 'string' && item.mimeType) ||
      blob.type ||
      (item.kind === 'pdf' ? 'application/pdf' : 'application/octet-stream');
    const name = item.name || (item.kind === 'pdf' ? 'document.pdf' : 'image');
    documents.push({ name, mimeType, file: new File([blob], name, { type: mimeType }) });
  }

  return documents;
};

export const prepareIncomingShare = async (
  share: { items?: ShareItem[] } | null | undefined,
  options: PrepareIncomingShareOptions = {}
): Promise<PreparedIncomingShare> => {
  const readFile = options.readFile || defaultReadFile;
  const toJpeg = options.toJpeg || downscaleToJpeg;
  const renderPdf = options.renderPdf || defaultRenderPdf;
  const mergeToPdf = options.mergeToPdf || defaultMergeToPdf;
  const password = typeof options.password === 'string' ? options.password : '';
  const items = Array.isArray(share?.items) ? share.items : [];

  const text = items
    .filter(item => item.kind === 'text' && typeof item.text === 'string')
    .map(item => (item.text || '').trim())
    .filter(Boolean)
    .join('\n')
    .slice(0, MAX_TEXT_CHARS);

  const pdfItem = items.find(item => item.kind === 'pdf' && typeof item.uri === 'string' && item.uri);
  const imageItems = items
    .filter(item => item.kind === 'image' && typeof item.uri === 'string' && item.uri)
    .slice(0, MAX_OCR_IMAGE_PAGES);

  const decisionImages: string[] = [];

  if (pdfItem && pdfItem.uri) {
    const blob = await readFile(pdfItem.uri);
    const pages = await renderPdf(await bytesOf(blob), password || undefined);
    pages.forEach(page => {
      if (decisionImages.length < MAX_DECISION_IMAGES) decisionImages.push(page.dataUrl);
    });
    assertOcrSize(blob);
    return {
      decisionImages,
      text,
      ocrFile: new File([blob], pdfItem.name || 'document.pdf', { type: 'application/pdf' }),
      pdfPassword: password || null
    };
  }

  if (!imageItems.length) throw shareFileError('NO_FILE');

  const jpegs: JpegPage[] = [];
  for (const item of imageItems) {
    if (!item.uri) continue;
    const jpeg = await toJpeg(await readFile(item.uri));
    jpegs.push(jpeg);
    if (decisionImages.length < MAX_DECISION_IMAGES) decisionImages.push(jpeg.dataUrl);
  }
  if (!jpegs.length) throw shareFileError('NO_FILE');

  if (jpegs.length === 1) {
    assertOcrSize(jpegs[0].blob);
    return {
      decisionImages,
      text,
      ocrFile: new File([jpegs[0].blob], jpegName(imageItems[0].name), { type: 'image/jpeg' }),
      pdfPassword: null
    };
  }

  const merged = await mergeToPdf(jpegs.map(jpeg => jpeg.blob));
  assertOcrSize(merged);
  return {
    decisionImages,
    text,
    ocrFile: new File([merged], 'shared.pdf', { type: 'application/pdf' }),
    pdfPassword: null
  };
};
